import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Save, SlidersHorizontal, Sparkles, ScanText } from "lucide-react";

const FIELDS = [
    { key: "default_rate_limit_per_minute", label: "Default RPM", hint: "Per API key per minute" },
    { key: "default_rate_limit_per_day", label: "Default RPD", hint: "Per API key per day" },
    { key: "default_monthly_limit", label: "Default monthly limit", hint: "Per API key per month" },
    { key: "default_max_documents_per_request", label: "Max docs / request" },
    { key: "default_max_file_size_mb", label: "Max file size (MB)" },
    { key: "default_max_processing_seconds", label: "Max processing seconds" },
    { key: "webhook_max_retries", label: "Webhook max retries" },
    { key: "webhook_retry_backoff_seconds", label: "Webhook backoff (s)" },
    { key: "ingest_queue_concurrency", label: "Ingest queue concurrency",
      hint: "Max number of ingest jobs (protocol / CTA / budget / datalake doc / CK URL) processed in parallel. Default 2. Changes take effect on the next job claim — no restart needed." },
];

// Reuse-revenue rate uses fractional dollars (e.g. $0.01), so it keeps
// its own field config with a smaller step + decimal precision.
const REUSE_RATE_FIELD = {
    key: "reuse_charge_usd_per_doc",
    label: "Reuse charge per doc (USD)",
    step: "0.001",
    hint: "System default applied when a tenant has no per-client override. Charges every cached AI output, datalake read, common-knowledge / RAG hit served to a paying tenant. 0 disables billing globally.",
};

// Chat-interaction cost — flat per-turn charge applied to every
// clinical / finance / legal / legacy protocol-chat call. 5-decimal
// precision so a single chat turn can be billed as little as 5 millicents.
const CHAT_INTERACTION_FIELD = {
    key: "chat_interaction_cost_usd",
    label: "Chat interaction cost (USD)",
    step: "0.00001",
    hint: "Flat charge applied to EVERY chat turn (clinical / finance / legal) regardless of cache hit. Stacks on top of the datalake reuse charge that already fires when a cached answer is reused. 5-decimal precision — e.g. 0.00005 = 5 millicents per question. 0 disables the flat fee globally.",
};

export default function SettingsPage() {
    const [state, setState] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/admin/settings").then((r) => setState(r.data));
    }, []);

    const save = async () => {
        setSaving(true);
        try {
            const payload = {};
            FIELDS.forEach((f) => { payload[f.key] = Number(state[f.key]); });
            // Reuse rate is a float — blank means "leave as-is", numeric (including 0) is honoured.
            if (state[REUSE_RATE_FIELD.key] !== "" && state[REUSE_RATE_FIELD.key] != null) {
                const rate = Number(state[REUSE_RATE_FIELD.key]);
                if (!Number.isFinite(rate) || rate < 0) {
                    throw new Error("Reuse charge must be a non-negative number");
                }
                payload[REUSE_RATE_FIELD.key] = rate;
            }
            payload.ai_provider = state.ai_provider;
            payload.ai_model = state.ai_model;
            payload.ocr_engine = state.ocr_engine;
            payload.vision_provider = state.vision_provider;
            payload.vision_model = state.vision_model;
            const { data } = await api.patch("/admin/settings", payload);
            setState(data);
            toast.success("Settings saved");
        } catch (e) {
            toast.error(e?.response?.data?.detail || e?.message || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    if (!state) {
        return <div className="px-8 py-8 text-xs text-white/40 font-mono">Loading settings…</div>;
    }

    return (
        <div className="px-8 py-8 max-w-4xl">
            <div className="mb-8">
                <div className="label-xs text-[#7C3AED]">Configuration</div>
                <h1 className="font-display text-4xl tracking-tight mt-2">Settings</h1>
                <p className="text-sm text-white/50 mt-2 max-w-2xl">
                    Global defaults applied when an API key has no per-key override. Changes
                    take effect immediately.
                </p>
            </div>

            <div className="bg-[#0a0a0a] border border-white/10 rounded-md p-6 mb-4">
                <div className="flex items-center gap-2 mb-4">
                    <SlidersHorizontal className="w-4 h-4 text-white/60" strokeWidth={1.5} />
                    <div className="font-display text-lg tracking-tight">Limits &amp; webhooks</div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                    {FIELDS.map((f) => (
                        <label key={f.key} className="block">
                            <span className="label-xs block mb-1.5">{f.label}</span>
                            <input
                                type="number"
                                value={state[f.key] ?? 0}
                                onChange={(e) => setState({ ...state, [f.key]: e.target.value })}
                                data-testid={`setting-${f.key}`}
                                className="w-full bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:border-white/30 focus:ring-2 focus:ring-white/10"
                            />
                            {f.hint && <div className="text-[11px] text-white/40 font-mono mt-1">{f.hint}</div>}
                        </label>
                    ))}
                </div>
            </div>

            <div className="bg-[#0a0a0a] border border-white/10 rounded-md p-6 mb-4" data-testid="setting-reuse-card">
                <div className="flex items-center gap-2 mb-4">
                    <SlidersHorizontal className="w-4 h-4 text-[#7C3AED]" strokeWidth={1.5} />
                    <div className="font-display text-lg tracking-tight">Datalake reuse revenue</div>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                    <label className="block">
                        <span className="label-xs block mb-1.5">{REUSE_RATE_FIELD.label}</span>
                        <input
                            type="number"
                            step={REUSE_RATE_FIELD.step}
                            min="0"
                            value={state[REUSE_RATE_FIELD.key] ?? ""}
                            onChange={(e) => setState({ ...state, [REUSE_RATE_FIELD.key]: e.target.value })}
                            data-testid={`setting-${REUSE_RATE_FIELD.key}`}
                            placeholder="0.01"
                            className="w-full bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:border-white/30 focus:ring-2 focus:ring-white/10"
                        />
                        <div className="text-[11px] text-white/40 font-mono mt-2 leading-relaxed">
                            {REUSE_RATE_FIELD.hint}
                        </div>
                    </label>
                    <label className="block">
                        <span className="label-xs block mb-1.5">{CHAT_INTERACTION_FIELD.label}</span>
                        <input
                            type="number"
                            step={CHAT_INTERACTION_FIELD.step}
                            min="0"
                            value={state[CHAT_INTERACTION_FIELD.key] ?? ""}
                            onChange={(e) => setState({ ...state, [CHAT_INTERACTION_FIELD.key]: e.target.value })}
                            data-testid={`setting-${CHAT_INTERACTION_FIELD.key}`}
                            placeholder="0.00005"
                            className="w-full bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:border-white/30 focus:ring-2 focus:ring-white/10"
                        />
                        <div className="text-[11px] text-white/40 font-mono mt-2 leading-relaxed">
                            {CHAT_INTERACTION_FIELD.hint}
                        </div>
                    </label>
                </div>
            </div>

            <div className="bg-[#0a0a0a] border border-white/10 rounded-md p-6 mb-6">
                <div className="flex items-center gap-2 mb-4">
                    <Sparkles className="w-4 h-4 text-[#7C3AED]" strokeWidth={1.5} />
                    <div className="font-display text-lg tracking-tight">AI pipeline</div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <label>
                        <span className="label-xs block mb-1.5">Provider</span>
                        <select
                            value={state.ai_provider}
                            onChange={(e) => setState({ ...state, ai_provider: e.target.value })}
                            data-testid="setting-provider"
                            className="w-full bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-sm font-mono"
                        >
                            <option value="anthropic">anthropic</option>
                            <option value="openai">openai</option>
                            <option value="gemini">gemini</option>
                        </select>
                    </label>
                    <label>
                        <span className="label-xs block mb-1.5">Model</span>
                        <input
                            type="text"
                            value={state.ai_model || ""}
                            onChange={(e) => setState({ ...state, ai_model: e.target.value })}
                            data-testid="setting-model"
                            className="w-full bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:border-white/30 focus:ring-2 focus:ring-white/10"
                        />
                    </label>
                </div>
                <div className="text-[11px] text-white/40 font-mono mt-3">
                    Supported examples: <span className="text-white/60">anthropic:claude-sonnet-5-5</span>, <span className="text-white/60">openai:gpt-5.1</span>, <span className="text-white/60">gemini:gemini-2.5-pro</span>
                </div>
            </div>

            <div className="bg-[#0a0a0a] border border-white/10 rounded-md p-6 mb-6">
                <div className="flex items-center gap-2 mb-4">
                    <ScanText className="w-4 h-4 text-[#7C3AED]" strokeWidth={1.5} />
                    <div className="font-display text-lg tracking-tight">OCR pipeline</div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                    <label>
                        <span className="label-xs block mb-1.5">Engine</span>
                        <select
                            value={state.ocr_engine || "vision_llm"}
                            onChange={(e) => setState({ ...state, ocr_engine: e.target.value })}
                            data-testid="setting-ocr-engine"
                            className="w-full bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-sm font-mono"
                        >
                            <option value="vision_llm">vision_llm (recommended)</option>
                            <option value="tesseract">tesseract</option>
                        </select>
                    </label>
                    <label>
                        <span className="label-xs block mb-1.5">Vision provider</span>
                        <select
                            value={state.vision_provider || "gemini"}
                            onChange={(e) => setState({ ...state, vision_provider: e.target.value })}
                            data-testid="setting-vision-provider"
                            className="w-full bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-sm font-mono"
                        >
                            <option value="gemini">gemini</option>
                            <option value="anthropic">anthropic</option>
                            <option value="openai">openai</option>
                        </select>
                    </label>
                    <label>
                        <span className="label-xs block mb-1.5">Vision model</span>
                        <input
                            type="text"
                            value={state.vision_model || ""}
                            onChange={(e) => setState({ ...state, vision_model: e.target.value })}
                            data-testid="setting-vision-model"
                            className="w-full bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-sm font-mono"
                        />
                    </label>
                </div>
                <div className="text-[11px] text-white/40 font-mono mt-3">
                    `vision_llm` transcribes screenshot-only PDFs page-by-page through a multimodal LLM (default <span className="text-white/60">gemini:gemini-2.5-flash</span>) — best quality. Falls back to Tesseract automatically on error. Only triggers when native PDF text averages &lt; 60 chars/page.
                </div>
            </div>

            <div className="flex items-center justify-end">
                <button
                    onClick={save}
                    disabled={saving}
                    data-testid="save-settings"
                    className="bg-white text-black px-5 py-2 rounded-md text-sm font-medium hover:bg-white/90 disabled:opacity-60 flex items-center gap-2"
                >
                    <Save className="w-4 h-4" strokeWidth={1.8} />
                    {saving ? "Saving…" : "Save settings"}
                </button>
            </div>
        </div>
    );
}
